import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import type { AvailableClass } from '@/types'

interface ClassSelectorProps {
  available: AvailableClass[]
  activeIds: number[]
  onChange: (ids: number[]) => void   // emits the new active id set
  disabled?: boolean
}

export function ClassSelector({ available, activeIds, onChange, disabled }: ClassSelectorProps) {
  const { t } = useTranslation('cameras')
  const [filter, setFilter] = useState('')
  const active = new Set(activeIds)
  const filtered = available.filter((c) => c.class_name.toLowerCase().includes(filter.toLowerCase()))
  const toggle = (id: number, on: boolean) =>
    onChange(on ? [...new Set([...activeIds, id])] : activeIds.filter((i) => i !== id))
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder={t('detection.classFilter')} className="h-9" disabled={disabled} />
        <div className="flex gap-2 text-xs">
          <button type="button" className="text-primary underline disabled:opacity-50" disabled={disabled}
                  onClick={() => onChange(available.map((c) => c.class_id))}>{t('detection.all')}</button>
          <button type="button" className="text-primary underline disabled:opacity-50" disabled={disabled}
                  onClick={() => onChange([])}>{t('detection.none')}</button>
        </div>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md border border-border/50 divide-y divide-border/50">
        {filtered.map((c) => (
          <label key={c.class_id} className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer">
            <span className="text-sm">{c.class_name}</span>
            <input type="checkbox" className="h-4 w-4 rounded accent-primary cursor-pointer"
                   checked={active.has(c.class_id)} disabled={disabled}
                   onChange={(e) => toggle(c.class_id, e.target.checked)} />
          </label>
        ))}
        {filtered.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">{t('detection.noMatch')}</p>
        )}
      </div>
    </div>
  )
}
